
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableApi from '@/components/table/SpmTableApi.vue';
import {
  SpmTableAction, SpmTableColumns, SpmTableFilter, SpmTableFilterOption, webhooksCreatedFromEnum, webhooksTypeEnum,
} from '@/types';
import { OperatorType, Webhooks } from '@/types/generated-types/graphql';
import { findPermission, UserState } from '@/composables/User';
import Tooltip from 'primevue/tooltip';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import ConfirmPopup from 'primevue/confirmpopup';
import { useConfirm } from 'primevue/useconfirm';
import { NestApiType, nestCall, toQueryString } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';

export default defineComponent({
  name: 'Webhooks',
  components: {
    PageHeader,
    SpmTableApi,
    SpmButton,
    ConfirmPopup,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const idShop = UserState.activeShop?.id ?? 0;
    const confirm = useConfirm();
    const spmTableApiRef = ref();

    const createdFromOptions: SpmTableFilterOption[] = Object.values(webhooksCreatedFromEnum)
      .map((key: string) => ({ value: key, label: t(`webhooks.enums.created_from.${key}`) }));

    const persistentFilters = ref<SpmTableFilter[]>([
      {
        field: 'type',
        value: webhooksTypeEnum.EXTERNAL,
        operator: OperatorType.Equals,
      },
      {
        field: 'deleted',
        value: false,
        operator: OperatorType.Equals,
      },
    ]);

    const editWebhook = (webhook: Webhooks) => {};

    const duplicateWebhook = async (webhook: Webhooks): Promise<Record<string, any>> => {
      const newName = t('webhooks.confirm.duplicate.cloneText') + webhook.name;
      const newWebhook = {
        id_shop: webhook.id_shop,
        name: newName,
        secret_key: 'AZERTYUIO',
        url: webhook.url,
        events: JSON.parse(webhook.events),
      };

      return nestCall('post', NestApiType.CLIENT_API, '/webhooks', {}, '', newWebhook);
    };

    const askDuplicateWebhook = (webhook: Webhooks, e: any) => {
      confirm.require({
        group: 'basicGroup',
        target: e.currentTarget,
        message: t('webhooks.confirm.duplicate.message'),
        icon: 'fal fa-info-circle',
        acceptLabel: t('validate'),
        acceptClass: 'p-button-success',
        rejectLabel: t('cancel'),
        rejectClass: 'p-button-secondary',
        accept: () => {
          duplicateWebhook(webhook).then((result: Record<string, any>) => {
            if (result.success) {
              showToastSuccess(t('webhooks.confirm.duplicate.success.message'));
              spmTableApiRef.value.reloadData();
            } else {
              showToastError(t('webhooks.confirm.duplicate.error.message'));
            }
          });
        },
      });
    };

    const deleteWebhook = async (webhook: Webhooks): Promise<Record<string, any>> => {
      const query = toQueryString({
        id_webhook: webhook.id_webhook,
      });

      return nestCall('delete', NestApiType.CLIENT_API, `/webhooks/${webhook.id_webhook}`, {}, `?${query}`);
    };

    const askDeleteWebhook = (webhook: Webhooks, e: any) => {
      confirm.require({
        group: 'basicGroup',
        target: e.currentTarget,
        message: t('webhooks.confirm.delete.message'),
        icon: 'fal fa-info-circle',
        acceptLabel: t('yes'),
        acceptClass: 'p-button-danger',
        rejectLabel: t('no'),
        rejectClass: 'p-button-secondary',
        accept: () => {
          deleteWebhook(webhook).then((result: Record<string, any>) => {
            if (result.success) {
              showToastSuccess(t('webhooks.confirm.delete.success.message'));
              spmTableApiRef.value.reloadData();
            } else {
              showToastError('GENERIC_ERROR');
            }
          });
        },
      });
    };

    const actions: SpmTableAction[] = [
      {
        icon: 'far fa-edit',
        label: t('webhooks.edit'),
        callback: (webhook: Webhooks) => editWebhook(webhook),

        show: () => findPermission('my_webhooks.edit'),
      },
      {
        icon: 'far fa-clone',
        label: t('webhooks.duplicate'),
        callback: (webhook: Webhooks, e: any) => (e && webhook ? askDuplicateWebhook(webhook, e) : false),
        show: () => findPermission('my_webhooks.duplicate'),
      },
      {
        icon: 'far fa-trash',
        label: t('webhooks.delete'),
        callback: (webhook: Webhooks, e: any) => (e && webhook ? askDeleteWebhook(webhook, e) : false),
        show: () => findPermission('my_webhooks.delete'),
      },
    ];

    const columns: SpmTableColumns[] = [
      {
        field: 'id_webhook',
        header: t('webhooks.headers.id_webhook'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'name',
        header: t('webhooks.headers.name'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'url',
        header: t('webhooks.headers.url'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'events',
        header: t('webhooks.headers.events'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'created_from',
        header: t('webhooks.headers.created_from'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        filterSettings: {
          type: 'multiSelect', options: createdFromOptions, hideFilterMenu: true, showFilterInput: false,
        },
      },
      {
        field: 'active',
        header: t('webhooks.headers.active'),
        sortable: true,
        filterable: true,
        editable: false,
        style: 'width:10%',
        type: 'active-status-icon',
        filterSettings: {
          type: 'multiSelect',
          options: [{ value: 1, label: t('active') }, { value: 0, label: t('inactive') }],
          hideFilterMenu: true,
          showFilterInput: false,
        },
      },
      {
        field: 'date_last_webhook',
        header: t('webhooks.headers.date_last_webhook'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'datetime',
        filterSettings: { type: 'date', hideFilterMenu: true },
      },
      {
        field: 'date_last_error',
        header: t('webhooks.headers.date_last_error'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'datetime',
        filterSettings: { type: 'date', hideFilterMenu: true },
      },
      {
        field: '', header: t('webhooks.headers.actions'), sortable: false, filterable: false, editable: false, style: 'width:10%', type: 'action', actions,
      },
    ];

    return {
      t,
      spmTableApiRef,
      idShop,
      columns,
      findPermission,
      persistentFilters,
    };
  },
});
